import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';
import { 
  CreditCard, Check, X, Clock, Eye, Download, Building, 
  IndianRupee, Calendar, FileText, CheckCircle, XCircle,
  Loader2, Search, Filter, RefreshCw, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentRequestsManagement() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Review form
  const [reviewForm, setReviewForm] = useState({
    status: 'approved',
    remarks: '',
    extend_days: ''
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch pending requests
      const pendingResponse = await fetch(`${API_URL}/api/subscription-payments/pending-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (pendingResponse.ok) {
        const data = await pendingResponse.json();
        setPendingRequests(data.requests || []);
      }
      
      // Fetch all requests
      const allResponse = await fetch(`${API_URL}/api/subscription-payments/all-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (allResponse.ok) {
        const data = await allResponse.json();
        setAllRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/subscription-payments/review-request/${selectedRequest.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: reviewForm.status,
          remarks: reviewForm.remarks,
          extend_days: reviewForm.extend_days ? parseInt(reviewForm.extend_days) : null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowReviewDialog(false);
        setSelectedRequest(null);
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process request');
      }
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setProcessing(false);
    }
  };

  const filteredRequests = (activeTab === 'pending' ? pendingRequests : allRequests).filter(request => {
    const matchesSearch = 
      request.tenant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.tenant_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.transaction_id?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || request.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Requests</h1>
          <p className="text-gray-500">Review and manage offline payment requests from tenants</p>
        </div>
        <Button onClick={fetchRequests} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingRequests.length}</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{allRequests.filter(r => r.status === 'approved').length}</p>
                <p className="text-sm text-gray-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{allRequests.filter(r => r.status === 'rejected').length}</p>
                <p className="text-sm text-gray-500">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <IndianRupee className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  ₹{allRequests.filter(r => r.status === 'approved').reduce((sum, r) => sum + (r.amount || 0), 0).toLocaleString('en-IN')}
                </p>
                <p className="text-sm text-gray-500">Total Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending ({pendingRequests.length})
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              All Requests
            </TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                className="pl-9 w-64"
                placeholder="Search by tenant, transaction ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <TabsContent value="pending">
          <Card>
            <CardContent className="p-0">
              {filteredRequests.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-500">No pending requests</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredRequests.map((request) => (
                    <RequestRow 
                      key={request.id} 
                      request={request}
                      onReview={() => {
                        setSelectedRequest(request);
                        setReviewForm({ status: 'approved', remarks: '', extend_days: '' });
                        setShowReviewDialog(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all">
          <Card>
            <CardContent className="p-0">
              {filteredRequests.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No requests found</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredRequests.map((request) => (
                    <RequestRow 
                      key={request.id} 
                      request={request}
                      onReview={request.status === 'pending' ? () => {
                        setSelectedRequest(request);
                        setReviewForm({ status: 'approved', remarks: '', extend_days: '' });
                        setShowReviewDialog(true);
                      } : null}
                      showStatus
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Payment Request</DialogTitle>
            <DialogDescription>
              Verify the payment details and approve or reject the request.
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Request Details */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Tenant</span>
                  <span className="font-medium">{selectedRequest.tenant_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Plan</span>
                  <span className="font-medium">{selectedRequest.plan_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-medium">₹{selectedRequest.amount?.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment Mode</span>
                  <span className="font-medium capitalize">{selectedRequest.payment_mode?.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Transaction ID</span>
                  <span className="font-medium">{selectedRequest.transaction_id || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment Date</span>
                  <span className="font-medium">{formatDate(selectedRequest.payment_date)}</span>
                </div>
                {selectedRequest.proof_url && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Proof</span>
                    <a 
                      href={`${API_URL}${selectedRequest.proof_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Eye className="h-4 w-4" /> View Proof
                    </a>
                  </div>
                )}
                {selectedRequest.notes && (
                  <div className="pt-2 border-t">
                    <span className="text-gray-500 text-sm">Notes:</span>
                    <p className="text-sm mt-1">{selectedRequest.notes}</p>
                  </div>
                )}
              </div>
              
              {/* Decision */}
              <div className="space-y-4">
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={reviewForm.status === 'approved' ? 'default' : 'outline'}
                    className={reviewForm.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
                    onClick={() => setReviewForm({...reviewForm, status: 'approved'})}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    type="button"
                    variant={reviewForm.status === 'rejected' ? 'destructive' : 'outline'}
                    onClick={() => setReviewForm({...reviewForm, status: 'rejected'})}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
                
                {reviewForm.status === 'approved' && (
                  <div className="space-y-2">
                    <Label>Custom Extension Days (Optional)</Label>
                    <Input 
                      type="number"
                      placeholder="Leave empty for default based on billing cycle"
                      value={reviewForm.extend_days}
                      onChange={(e) => setReviewForm({...reviewForm, extend_days: e.target.value})}
                    />
                    <p className="text-xs text-gray-500">
                      Default: Monthly=30, Quarterly=90, Yearly=365 days
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Remarks {reviewForm.status === 'rejected' && '(Required)'}</Label>
                  <Textarea 
                    placeholder={reviewForm.status === 'rejected' ? 'Reason for rejection...' : 'Any notes...'}
                    value={reviewForm.remarks}
                    onChange={(e) => setReviewForm({...reviewForm, remarks: e.target.value})}
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReview} 
              disabled={processing || (reviewForm.status === 'rejected' && !reviewForm.remarks)}
              className={reviewForm.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {processing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {reviewForm.status === 'approved' ? 'Approve & Activate' : 'Reject Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Request Row Component
function RequestRow({ request, onReview, showStatus }) {
  return (
    <div className="p-4 hover:bg-gray-50 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="p-2 bg-gray-100 rounded-lg">
          <Building className="h-5 w-5 text-gray-600" />
        </div>
        <div>
          <p className="font-medium">{request.tenant_name}</p>
          <p className="text-sm text-gray-500">
            {request.plan_name} • ₹{request.amount?.toLocaleString('en-IN')} • {request.billing_cycle}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {request.payment_mode?.replace('_', ' ')} • {request.transaction_id || 'No TXN ID'} • {new Date(request.submitted_at).toLocaleString('en-IN')}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        {showStatus && (
          <Badge variant={
            request.status === 'approved' ? 'success' :
            request.status === 'rejected' ? 'destructive' : 'secondary'
          }>
            {request.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
            {request.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
            {request.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
          </Badge>
        )}
        
        {request.proof_url && (
          <a 
            href={`${API_URL}${request.proof_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-gray-200 rounded-lg"
            title="View Proof"
          >
            <Eye className="h-4 w-4 text-gray-500" />
          </a>
        )}
        
        {onReview && (
          <Button size="sm" onClick={onReview}>
            Review
          </Button>
        )}
      </div>
    </div>
  );
}
